import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HotelIcon from '@mui/icons-material/Hotel';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { APIHOST } from '../common';
import { Hotel } from '../types/hotel';
import { OrderRoom } from '../types/orderRoom';
import DisplayCreditCard from '../components/dialog/DisplayCreditCard';
import ExportBookings from '../components/dialog/ExportBookings';
import ImportConfirmations from '../components/dialog/ImportConfirmations';
import { getToken } from '../components/auth/AuthContext';
import { useApp } from '../components/AppContext';
import { UpdateConfirmationRequest } from '../types/updateConfirmationRequest';

type BookingStatsResponse = RoomStats[];
interface RoomStats {
    roomId: string;
    roomName: string;
    dailyRate: number;
    totalBookings: number;
    percentBooked: number;
    totalRoomNightsAllocated: number;
    totalRoomNightsAvailable: number;
    totalRoomNightsBooked: number;
    allocations: Record<string, number>
    bookings: Record<string, number>
}

export default function HotelBookingList() {
  const { hotelId } = useParams<{ hotelId: string }>();
  const navigate = useNavigate();
  const { setSnackbarInput } = useApp();

  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [orders, setOrders] = useState<OrderRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [bookingStatsLoading, setBookingStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [ordersError, setOrdersError] = useState('');
  const [bookingStatsError, setBookingStatsError] = useState('');
  const [bookingStats, setBookingStats] = useState<BookingStatsResponse>([]);
  const [creditCardDialogOrderId, setCreditCardDialogOrderId] = useState<string | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [bookingsFilter, setBookingsFilter] = useState<'all' | 'missing-hotel-confirmation'>('all');
  const [reloadKey, setReloadKey] = useState(0);
  const [hotelConfirmations, setHotelConfirmations] = useState<Record<string, string>>({});
  const [savingConfirmations, setSavingConfirmations] = useState<Record<string, boolean>>({});
  const [savedConfirmations, setSavedConfirmations] = useState<Record<string, boolean>>({});

  const getRoomKey = (orderRoom: OrderRoom) => orderRoom.documentId || String(orderRoom.id);

  useEffect(() => {
    const fetchHotelAndOrders = async () => {
      if (!hotelId) {
        setError('Hotel ID is required');
        setOrdersError('Hotel ID is required');
        setBookingStatsError('Hotel ID is required');
        setLoading(false);
        setOrdersLoading(false);
        setBookingStatsLoading(false);
        return;
      }

      try {
        setLoading(true);
        setOrdersLoading(true);
        setBookingStatsLoading(true);
        setError('');
        setOrdersError('');
        setBookingStatsError('');

        const token = getToken();
        const [hotelResponse, ordersResponse, bookingStatsResponse] = await Promise.all([
          fetch(`${APIHOST}/api/hotel-admin/hotels/${hotelId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }),
          fetch(`${APIHOST}/api/hotel-admin/order-rooms?hotelId=${hotelId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }),
          fetch(`${APIHOST}/api/hotel-admin/hotels/${hotelId}/booking-stats`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
        ]);

        if (!hotelResponse.ok) {
          throw new Error(`Failed to fetch hotel: ${hotelResponse.statusText}`);
        }

        if (!ordersResponse.ok) {
          throw new Error(`Failed to fetch order rooms: ${ordersResponse.statusText}`);
        }

        if (!bookingStatsResponse.ok) {
          setBookingStatsError(`Failed to fetch booking stats: ${bookingStatsResponse.statusText}`);
        }

        const [hotelPayload, ordersPayload] = await Promise.all([
          hotelResponse.json(),
          ordersResponse.json()
        ]);

        setHotel(hotelPayload.data);

        const hotelOrderRooms: OrderRoom[] = Array.isArray(ordersPayload?.data)
          ? (ordersPayload.data as OrderRoom[])
          : [];

        setOrders(hotelOrderRooms);
        const confirmationMap: Record<string, string> = {};
        hotelOrderRooms.forEach((room) => {
          const key = getRoomKey(room);
          confirmationMap[key] = room.hotelConfirmation || '';
        });
        setHotelConfirmations(confirmationMap);

        if (bookingStatsResponse.ok) {
          const bookingStatsPayload = await bookingStatsResponse.json();
          const statsData = bookingStatsPayload?.data ?? bookingStatsPayload;
          if (Array.isArray(statsData)) {
            setBookingStats(statsData.filter((item): item is RoomStats => Boolean(item) && typeof item === 'object'));
          } else if (statsData && typeof statsData === 'object') {
            setBookingStats([statsData as RoomStats]);
          } else {
            setBookingStats([]);
          }
        } else {
          setBookingStats([]);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load hotel data';
        setError(message);
        setOrdersError(message);
        setBookingStatsError(message);
        setHotel(null);
        setOrders([]);
        setBookingStats([]);
      } finally {
        setLoading(false);
        setOrdersLoading(false);
        setBookingStatsLoading(false);
      }
    };

    fetchHotelAndOrders();
  }, [hotelId, reloadKey]);

  const handleHotelConfirmationBlur = async (orderRoom: OrderRoom) => {
    const roomKey = getRoomKey(orderRoom);
    const internalId = orderRoom.confirmation || '';
    const hotelConfirmationNumber = hotelConfirmations[roomKey] ?? '';

    if (!internalId) {
      setSnackbarInput({
        message: 'Missing internal confirmation id for this room',
        severity: 'error',
      });
      return;
    }

    setSavingConfirmations((prev) => ({ ...prev, [roomKey]: true }));
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const payload: UpdateConfirmationRequest = {
        data: {
          internalId,
          hotelConfirmationNumber,
        },
      };

      const response = await fetch(`${APIHOST}/api/hotel-admin/update-confirmation`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to save hotel confirmation');
      }

      setSavedConfirmations((prev) => ({ ...prev, [roomKey]: true }));
      setSnackbarInput({ message: 'Hotel confirmation saved', severity: 'success' });
    } catch (err) {
      setSnackbarInput({
        message: err instanceof Error ? err.message : 'Failed to save hotel confirmation',
        severity: 'error',
      });
    } finally {
      setSavingConfirmations((prev) => ({ ...prev, [roomKey]: false }));
    }
  };

  const orderSummary = useMemo(() => {
    const summary = {
      total: 0,
      draft: 0,
      received: 0,
      pending: 0,
      confirmed: 0,
      abandoned: 0
    };

    for (const orderRoom of orders) {
      summary.total += 1;
      if (orderRoom.order?.orderStatus === 'draft') summary.draft += 1;
      if (orderRoom.order?.orderStatus === 'received') summary.received += 1;
      if (orderRoom.order?.orderStatus === 'pending') summary.pending += 1;
      if (orderRoom.order?.orderStatus === 'confirmed') summary.confirmed += 1;
      if (orderRoom.order?.orderStatus === 'abandoned') summary.abandoned += 1;
    }

    return summary;
  }, [orders]);

  const bookingConfirmationSummary = useMemo(() => {
    const total = orders.length;
    let confirmed = 0;

    for (const room of orders) {
      const roomKey = getRoomKey(room);
      const confirmationValue = (hotelConfirmations[roomKey] ?? room.hotelConfirmation ?? '').trim();
      if (confirmationValue.length > 0) {
        confirmed += 1;
      }
    }

    return {
      total,
      confirmed,
      unconfirmed: total - confirmed,
    };
  }, [orders, hotelConfirmations]);

  const recentOrders = useMemo(() => {
    return [...orders]
      .sort((a, b) => new Date(b.order?.createdAt || 0).getTime() - new Date(a.order?.createdAt || 0).getTime())
  }, [orders]);

  const groupedByOrder = useMemo(() => {
    const groups = new Map<string, { order: OrderRoom['order']; rooms: OrderRoom[] }>();
    for (const room of recentOrders) {
      const key = room.order?.documentId || '__unknown__';
      if (!groups.has(key)) {
        groups.set(key, { order: room.order, rooms: [] });
      }
      groups.get(key)!.rooms.push(room);
    }
    return Array.from(groups.values());
  }, [recentOrders]);

  const filteredGroupedByOrder = useMemo(() => {
    if (bookingsFilter === 'all') {
      return groupedByOrder;
    }

    return groupedByOrder
      .map((group) => {
        const filteredRooms = group.rooms.filter((room) => {
          const roomKey = getRoomKey(room);
          const confirmationValue = (hotelConfirmations[roomKey] ?? room.hotelConfirmation ?? '').trim();
          return confirmationValue.length === 0;
        });
        return {
          ...group,
          rooms: filteredRooms,
        };
      })
      .filter((group) => group.rooms.length > 0);
  }, [groupedByOrder, bookingsFilter, hotelConfirmations]);

  const filteredOrdersForExport = useMemo(() => {
    return filteredGroupedByOrder.flatMap((group) => group.rooms);
  }, [filteredGroupedByOrder]);

  const roomTypeNightTotals = useMemo(() => {
    const nightsByType = new Map<string, number>();

    const getNights = (arrivalDate?: string, departureDate?: string) => {
      if (!arrivalDate || !departureDate) return 0;
      const arrival = new Date(`${arrivalDate}T00:00:00`);
      const departure = new Date(`${departureDate}T00:00:00`);
      if (Number.isNaN(arrival.getTime()) || Number.isNaN(departure.getTime())) return 0;
      const msPerDay = 1000 * 60 * 60 * 24;
      return Math.max(0, Math.round((departure.getTime() - arrival.getTime()) / msPerDay));
    };

    for (const room of orders) {
      const roomType = room.conference_hotel_room?.name || room.type || 'Unknown';
      const nights = getNights(room.arrivalDate, room.departureDate);
      nightsByType.set(roomType, (nightsByType.get(roomType) || 0) + nights);
    }

    return Array.from(nightsByType.entries())
      .map(([roomType, roomNights]) => ({ roomType, roomNights }))
      .sort((a, b) => b.roomNights - a.roomNights || a.roomType.localeCompare(b.roomType));
  }, [orders]);

  const bookingStatsDates = useMemo(() => {
    const allDates = new Set<string>();
    bookingStats.forEach((room) => {
      Object.keys(room.bookings || {}).forEach((date) => allDates.add(date));
    });
    return Array.from(allDates).sort((a, b) => a.localeCompare(b));
  }, [bookingStats]);

  const bookingStatsTotals = useMemo(() => {
    const totalByDate = bookingStatsDates.reduce<Record<string, number>>((acc, date) => {
      acc[date] = bookingStats.reduce((dateSum, room) => dateSum + (room.bookings?.[date] ?? 0), 0);
      return acc;
    }, {});

    return {
      totalByDate
    };
  }, [bookingStats, bookingStatsDates]);

  const bookingStatsSummary = useMemo(() => {
    const totalRoomNightsBooked = bookingStats.reduce(
      (sum, room) => sum + (room.totalRoomNightsBooked || 0),
      0
    );
    const totalRoomNightsAllocated = bookingStats.reduce(
      (sum, room) => sum + (room.totalRoomNightsAllocated || 0),
      0
    );
    const utilizationPercent =
      totalRoomNightsAllocated > 0
        ? (totalRoomNightsBooked / totalRoomNightsAllocated) * 100
        : 0;

    return {
      totalRoomNightsBooked,
      totalRoomNightsAllocated,
      utilizationPercent
    };
  }, [bookingStats]);

  const formatDailyRate = (value: number) => {
    if (!Number.isFinite(value)) {
      return '$0.00';
    }
    return `$${value.toFixed(2)}`;
  };

  const formatDateWithoutYear = (date: string) => {
    const [year, month, day] = date.split('-');
    if (!year || !month || !day) {
      return date;
    }
    return `${month}/${day}`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !hotel) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/hotel-admin')} sx={{ mb: 2 }}>
          Back to Hotels
        </Button>
        <Alert severity="error">{error || 'Hotel not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/hotel-admin')} sx={{ mb: 3 }}>
        Back to Hotels
      </Button>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HotelIcon />
          {hotel.name}
        </Typography>
        {hotel.longName && hotel.longName !== hotel.name && (
          <Typography variant="body1" color="text.secondary">
            {hotel.longName}
          </Typography>
        )}
      </Box>

      <Grid container spacing={3}>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Room Booking Stats
              </Typography>
              {!ordersLoading && !ordersError && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Bookings: {bookingConfirmationSummary.total} | Confirmed: {bookingConfirmationSummary.confirmed} | Unconfirmed: {bookingConfirmationSummary.unconfirmed}
                </Typography>
              )}
              {!bookingStatsLoading && !bookingStatsError && bookingStats.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {`${bookingStatsSummary.totalRoomNightsBooked} / ${bookingStatsSummary.totalRoomNightsAllocated} room nights booked (${bookingStatsSummary.utilizationPercent.toFixed(1)}%)`}
                </Typography>
              )}
              {bookingStatsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading booking stats...
                  </Typography>
                </Box>
              ) : bookingStatsError ? (
                <Alert severity="error">{bookingStatsError}</Alert>
              ) : bookingStats.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No booking stats available.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Room</TableCell>
                      <TableCell>Daily Rate</TableCell>
                      {bookingStatsDates.map((date) => (
                        <TableCell key={date}>{formatDateWithoutYear(date)}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookingStats.map((row, index) => (
                      <TableRow key={`booking-stat-row-${index}`}>
                        <TableCell>{row.roomName}</TableCell>
                        <TableCell>{formatDailyRate(row.dailyRate)}</TableCell>
                        {bookingStatsDates.map((date) => (
                          <TableCell key={`${date}-${index}`}>
                            {row.bookings?.[date] ?? 0}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} />
                      {bookingStatsDates.map((date) => (
                        <TableCell key={`total-${date}`} sx={{ fontWeight: 700 }}>
                          {bookingStatsTotals.totalByDate[date] ?? 0}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box sx={{ float: 'left', mb: 2 }}>
                <Typography variant="h6">
                  Booked Rooms
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 8, gap: 3 }}>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Bookings Filter</InputLabel>
                  <Select
                    value={bookingsFilter}
                    label="Bookings Filter"
                    onChange={(e) => setBookingsFilter(e.target.value as 'all' | 'missing-hotel-confirmation')}
                  >
                    <MenuItem value="all">Show all bookings</MenuItem>
                    <MenuItem value="missing-hotel-confirmation">Missing hotel confirmation</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => setImportDialogOpen(true)}
                >
                  Import Confirmations
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => setExportDialogOpen(true)}
                  disabled={filteredOrdersForExport.length === 0}
                >
                  Export ({filteredOrdersForExport.length})
                </Button>
              </Box>

              {ordersLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading order summary...
                  </Typography>
                </Box>
              )}

              {!ordersLoading && ordersError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {ordersError}
                </Alert>
              )}

              {!ordersLoading && !ordersError && (
                <Stack spacing={2}>
                  <Box>
                    {filteredGroupedByOrder.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No bookings found for the selected filter.
                      </Typography>
                    ) : (
                      <Stack spacing={3}>
                        {filteredGroupedByOrder.map(({ order, rooms }) => (
                          <Box key={order?.documentId || '__unknown__'}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 0.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Order #{order?.confirmation || 'Unknown'}
                                {order && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {[order.contactFirstName, order.contactLastName].filter(Boolean).join(' ')}
                                  </Typography>
                                )}
                              </Typography>
                              {order && <>
                                <Button
                                  size="small"
                                  variant="text"
                                  href={`/hotel-admin/orders/${order.documentId}`}
                                >
                                  View Order
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => setCreditCardDialogOrderId(order.documentId)}
                                  disabled={!order.creditCardAdded}
                                >
                                  View Credit Card
                                </Button>
                              </>}
                            </Box>
                            <Stack spacing={1}>
                              {rooms.map((orderRoom) => (
                                <Box
                                  key={getRoomKey(orderRoom)}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 1
                                  }}
                                >
                                  <Box sx={{ width: '100%' }}>
                                    <Box sx={{ float: 'right' }}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ mt: 1 }}
                                        href={`/hotel-admin/room/${orderRoom.documentId}`}
                                      >
                                        View
                                      </Button>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      #{orderRoom.confirmation || 'No Confirmation'}
                                    </Typography>
                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" sx={{ minWidth: 130 }}>
                                        Hotel Confirmation
                                      </Typography>
                                      <TextField
                                        size="small"
                                        variant="outlined"
                                        value={hotelConfirmations[getRoomKey(orderRoom)] ?? ''}
                                        onChange={(e) => {
                                          const roomKey = getRoomKey(orderRoom);
                                          setHotelConfirmations((prev) => ({
                                            ...prev,
                                            [roomKey]: e.target.value,
                                          }));
                                          setSavedConfirmations((prev) => ({ ...prev, [roomKey]: false }));
                                        }}
                                        onBlur={() => handleHotelConfirmationBlur(orderRoom)}
                                        inputProps={{ style: { padding: '4px 8px' } }}
                                      />
                                      {savingConfirmations[getRoomKey(orderRoom)] ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <CheckCircleIcon
                                          fontSize="small"
                                          sx={{ color: savedConfirmations[getRoomKey(orderRoom)] ? 'success.main' : 'action.disabled' }}
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="caption">
                                      {orderRoom.conference_hotel_room?.name || 'Unknown'}
                                      <br />
                                      Check In: {orderRoom.arrivalDate}
                                      <br />
                                      Check Out: {orderRoom.departureDate}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Type: {orderRoom.type || 'Unknown'}
                                    </Typography>
                                    {Array.isArray(orderRoom.order_room_occupants) && orderRoom.order_room_occupants.length > 0 && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2}}>
                                        Occupants:
                                      </Typography>
                                    )}
                                    {Array.isArray(orderRoom.order_room_occupants) && orderRoom.order_room_occupants.length > 0 && (
                                      <Box sx={{ mt: 0.5 }}>
                                        {orderRoom.order_room_occupants.map((occ) => (
                                          <Typography key={occ.id} variant="caption" color="text.secondary" display="block">
                                            {occ.lastName}, {occ.firstName}
                                          </Typography>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
        <DisplayCreditCard
          open={Boolean(creditCardDialogOrderId)}
          onClose={() => setCreditCardDialogOrderId(null)}
          orderId={creditCardDialogOrderId || ""}
        />

        <ExportBookings
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          hotelId={hotel?.documentId || hotelId || ''}
          orders={filteredOrdersForExport}
        />

        <ImportConfirmations
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          // onImported={() => setReloadKey((prev) => prev + 1)}
        />
      
    </Container>
  );
}
